/* eslint-disable react-hooks/exhaustive-deps */
import { Save } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { ImagePicker } from "components";
import React, { useEffect, useState } from "react";
import { useForm, usePriorities } from "hooks";
import DialogHeader from "./DialogHeader";
import { useBreakpoint, useTranslations } from "hooks";
import { getTranslation } from "common";
import { Validation, isRequired } from "hooks/useForm";
import { Priority } from "hooks/usePriorities";
import { Organization } from "model/organizationModel";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

/**
 * Modale di **creazione** di un'organizzazione (icona e nome). La modifica di
 * un'organizzazione esistente non passa piu' da qui: e' la vista
 * `/dashboard/organizations/:organizationId/settings` (`OrganizationSettings.tsx`),
 * che ha ereditato priorita', etichette, calendario e uscita dall'organizzazione.
 */
interface OrganizationDialogProps {
  open: boolean;
  organization: Organization | null;
  onClose: () => void;
  onSave: (
    photo: string | null,
    name: string | null,
    priorities: Priority[]
  ) => Promise<void>;
  loading: boolean;
}

const OrganizationDialog: React.FC<OrganizationDialogProps> = ({
  open,
  organization,
  onClose,
  onSave,
  loading,
}) => {
  const translations = useTranslations();
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const validations: Validation[] = [
    ({ name }) =>
      isRequired(name) || {
        name: getTranslation(translations, "placeholders.nameisrequired.label"),
      },
  ];
  const [priorities, prioritiesLoading] = usePriorities(
    organization?.id || null
  );

  const isMobile = useBreakpoint("down", "sm");
  const { reset, isValid, changeHandler, values, errors, touched, setValues } =
    useForm(
      {
        priorities: [],
        name: organization?.name || "",
        icon: organization?.icon || "",
      },
      validations
    );

  useEffect(() => {
    if (!prioritiesLoading && priorities.length >= 0) {
      setValues((_: any) => ({ ..._, priorities }));
    }
  }, [prioritiesLoading, priorities]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    if (organization) {
      setValues(organization);
    }
  }, [organization]);

  return (
    <>
    <Dialog
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => onClose()}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
        },
      }}
    >
      <DialogHeader
        onClose={onClose}
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          fontSize: 24,
          fontWeight: 800,
        }}
      >
        {organization?.id
          ? organization.name
          : getTranslation(translations, "organizations.edit.suggestnew")}
      </DialogHeader>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
          <Box data-testid="organization-image-settings" sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
              }}
            >
              <ImagePicker
                photoUrl={values?.icon || ""}
                relativePath={organization?.icon || ""}
                setPhotoUrl={(value) => changeHandler("icon", value)}
              />
            </Box>
          </Box>
          <Grid flex={1} spacing={3} container data-testid="organization-identity-settings">
            <Grid item xs={12}>
              <TextField
                id="organizations-dialog"
                variant="outlined"
                fullWidth
                size="small"
                label={getTranslation(
                  translations,
                  "placeholders.organization.name.label"
                )}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                value={values?.name || ""}
                onChange={({ target }) => changeHandler("name", target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FlowentiButton
                id="btn-save-organization"
                variant="contained"
                disabled={!isValid || saveLoading || loading}
                startIcon={<Save />}
                size="small"
                onClick={async () => {
                  setSaveLoading(true);
                  try {
                    await onSave(values.icon, values.name, values.priorities);
                  } finally {
                    setSaveLoading(false);
                  }
                }}
              >
                {getTranslation(translations, "pm.button.save")}
              </FlowentiButton>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default OrganizationDialog;
